import { useState, useEffect, useRef } from 'react';

/**
 * Like useState, but persists the value in sessionStorage under `key`.
 * - State survives across in-tab navigation (e.g. opening a detail view and
 *   pressing Back), but is cleared when the tab/window is closed.
 * - When `key` changes (e.g. navigating to a different form template), the
 *   state is re-initialized from the new key's stored value.
 */
export default function useStickyState(key, defaultValue) {
  const storageKey = `sticky:${key}`;

  const readInitial = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw === null) return defaultValue;
      return JSON.parse(raw);
    } catch {
      return defaultValue;
    }
  };

  const [value, setValue] = useState(readInitial);

  // Re-read when the key changes (e.g. user navigates between templates)
  const lastKey = useRef(storageKey);
  useEffect(() => {
    if (lastKey.current !== storageKey) {
      lastKey.current = storageKey;
      setValue(readInitial());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist on change
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      /* sessionStorage may be full or unavailable — ignore */
    }
  }, [storageKey, value]);

  return [value, setValue];
}
